import React, { useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Heart, HandCoins } from 'lucide-react';
import TopTabStrip from '../../components/TopTabStrip';
import {
  services, myEngagements
} from '../../data/mockData';
import { useOfferings } from '../../components/OfferingsProvider';
import OfferingCard from '../../components/OfferingCard';
import { loadSavedItems, toggleSavedItem } from '../../utils/saved';

// Maps a legacy Explore "service" tile to a top-tab category
const SERVICE_CATEGORY = {
  'financial-assistance': 'services',
  'ihsan-casket': 'services',
  'kidney-care': 'services',
  'bursaries': 'services',
  'ulum-asatizah': 'services',
  'daily-attendance': 'services',
  'volunteer': 'volunteer',
  'corporate-csr': 'give',
  'sponsor': 'give',
  'ulum-ihsan': 'learn',
  'filament': 'learn',
  'impact-stories': 'learn'
};

// Thumbnails for the Explore tiles that have no richer hub record behind them.
const LEGACY_SERVICE_IMAGE = {
  volunteer: '/images/volunteer-hub-hero.jpeg',
  'corporate-csr': 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80',
  sponsor: 'https://images.unsplash.com/photo-1593113598332-cd288d649433?w=800&q=80',
  'ulum-ihsan': '/images/ulum-ihsan-dua-surah-hero.jpeg',
  filament: 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=800&q=80',
  'impact-stories': '/images/hero-slide-2-everyday-ihsan.jpeg'
};
const FALLBACK_IMAGE = '/images/hero-slide-1-community.jpeg';

const EMPTY_LABEL = {
  all: "Nothing saved yet. Tap the heart icon on any card in Explore to save it here.",
  services: "You haven't applied for any assistance yet.",
  learn: "No saved courses yet. Tap the heart icon on a course in Learn to save it here.",
  volunteer: "No saved opportunities yet. Tap the heart icon in Volunteer to save one.",
  give: "No saved causes yet. Tap the heart icon on a way to give to save it here."
};

export default function Saved({ activeTopTab = 'all', onTopTabChange, onOpenExploreItem }) {
  const [savedIds, setSavedIds] = useState(() => loadSavedItems());
  const {
    serviceHubItems,
    volunteerHubItems,
    learnHubItems,
    givingOptions,
    dialysisCentres,
    casketPackages
  } = useOfferings();

  const handleUnsave = (id) => {
    setSavedIds(toggleSavedItem(id));
  };

  // Every saved entry is normalised to the same card shape — image, tag, title,
  // org, date — so the grid stays uniform whatever the item came from. The
  // first source to claim an id wins, which keeps the richer hub records ahead
  // of the legacy `services` tiles they replaced.
  const buildEntries = () => {
    const entries = [];
    const push = (item, category, hub) => {
      if (entries.some((e) => e.id === item.id)) return;
      entries.push({ ...item, category, hub: hub || category });
    };

    serviceHubItems.forEach((s) => push(s, 'services', 'services'));
    learnHubItems.forEach((l) => push(l, 'learn', 'learn'));
    volunteerHubItems.forEach((v) => push({ ...v, org: v.org || v.location }, 'volunteer', 'volunteer'));
    givingOptions.forEach((g) => push(g, 'give', 'give'));
    dialysisCentres.forEach((d) => push(d, 'services', 'dialysis'));
    casketPackages.forEach((c) => push(c, 'services', 'casket'));
    // Legacy Explore tiles that have no hub record of their own.
    // Kidney Care is the Dialysis hub now — skip the old Services tile.
    services.filter((s) => s.id !== 'kidney-care').forEach((s) => push({
      id: s.id,
      title: s.title,
      org: s.description,
      image: LEGACY_SERVICE_IMAGE[s.id] || FALLBACK_IMAGE,
      tag: s.pillar === 'learn' ? 'Learn' : s.pillar === 'give' ? 'Give' : 'Services',
      tagColor: s.pillar === 'learn' ? 'bg-emerald-600' : s.pillar === 'give' ? 'bg-amber-600' : 'bg-primary'
    }, SERVICE_CATEGORY[s.id] || null));

    return entries.filter((e) => savedIds.has(e.id));
  };

  const savedEntries = buildEntries();
  const appliedAssistance = myEngagements.services || [];

  const visibleSaved = activeTopTab === 'all'
    ? savedEntries
    : savedEntries.filter((e) => e.category === activeTopTab);
  const visibleApplied = (activeTopTab === 'all' || activeTopTab === 'services') ? appliedAssistance : [];

  const isEmpty = visibleSaved.length === 0 && visibleApplied.length === 0;

  return (
    <div className="bg-background page-fade-in" data-testid="saved-page">
      <TopTabStrip activeTab={activeTopTab} onChange={onTopTabChange} />

      {isEmpty ? (
        <div className="flex flex-col items-center justify-center py-20 px-4 text-center" data-testid="saved-empty-state">
          <Heart className="h-10 w-10 text-muted-foreground/40 mb-3" />
          <p className="text-sm font-semibold text-foreground">Nothing here yet</p>
          <p className="text-xs text-muted-foreground mt-1 max-w-[240px]">
            {EMPTY_LABEL[activeTopTab] || EMPTY_LABEL.all}
          </p>
        </div>
      ) : (
        <div className="px-4 pb-6 pt-3 space-y-4 desktop-shell">
          {visibleApplied.length > 0 && (
            <div className="flex flex-col gap-2" data-testid="saved-applied-list">
              {visibleApplied.map((item) => (
                <Card key={item.id} data-testid={`saved-applied-${item.id}`}>
                  <CardContent className="p-3 flex items-center gap-3">
                    <div className="w-11 h-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                      <HandCoins className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#0F172A] truncate">{item.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{item.meta}</p>
                    </div>
                    <span className="text-[11px] font-semibold text-primary whitespace-nowrap">{item.status}</span>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          {visibleSaved.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-testid="saved-cards-grid">
              {visibleSaved.map((item) => (
                <OfferingCard
                  key={item.id}
                  item={item}
                  isSaved
                  onToggleSave={handleUnsave}
                  onClick={() => onOpenExploreItem?.({ type: item.hub || item.category, item })}
                  testId={`saved-card-${item.id}`}
                  saveTestId={`unsave-${item.id}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
